import { isObject, uniqBy } from 'lodash-es';

export const isOptionWithPrice = ({ price }) =>
  !!price && (price.usedPrivateParty > 0 || price.usedTmvRetail > 0 || price.usedTradeIn > 0);

export const isEngineOrTransmissionOption = ({ options = [] }) =>
  options.some(o => isObject(o.transmission) || isObject(o.engine));

export const isLowValueOption = option => !isOptionWithPrice(option);

export const getSelectedOptions = (options = []) => options.filter(({ selected }) => selected);

/**
 * Compares two options lists by option id
 * @param {Array} optionsListValue - list of options
 * @param {Array} optionsListToCompare - list of options to compare
 * @return {Boolean} Returns true if the options are equivalent, else false
 */
export const isOptionsEqual = (optionsListValue, optionsListToCompare) => {
  if (!optionsListValue || !optionsListToCompare || optionsListValue.length !== optionsListToCompare.length) {
    return false;
  }
  return optionsListValue.every((value, index) => {
    const currOptionsListToCompare = optionsListToCompare[index];
    return currOptionsListToCompare && value.id === currOptionsListToCompare.id;
  });
};

/**
 * Transforms colors object of vehicleFeatures received from VehicleModel
 * to sort colors
 *
 * The color sorting is very basic and just happens by comparing
 * rgb string values. This leads to a pattern of dark, to light,
 * to dark palette layouts. It works like this:
 * - If one of the two compared rgbs is undefined, that goes second
 * - If one of the two compared rgbs is transparent, it goes first
 * - If the two compared rgbs are equal, just return as is
 * - By default, return which rgb comes first "alpha-numerically".
 * - e.g. 0,0,0 < 123,123,123 < 2,0,0 (hence dark -> light -> dark)
 *
 * Returns same object
 * @param {Object} vehicleFeatures
 */
export const transformColorsData = vehicleFeatures => {
  const sortColors = (color1, color2) => {
    const rgb1 = color1?.properties?.rgb;
    const rgb2 = color2?.properties?.rgb;

    if (!rgb1) return 1;
    if (!rgb2) return -1;
    if (rgb1 === 'transparent') return -1;
    if (rgb2 === 'transparent') return 1;
    if (rgb1 === rgb2) return 0;
    return rgb1 < rgb2 ? -1 : 1;
  };

  const transformedExteriorColors = (vehicleFeatures?.exteriorColors ?? []).sort(sortColors);
  const transformedInteriorColors = (vehicleFeatures?.interiorColors ?? []).sort(sortColors);
  return {
    ...vehicleFeatures,
    exteriorColors: uniqBy(transformedExteriorColors, 'name'),
    interiorColors: uniqBy(transformedInteriorColors, 'name'),
  };
};

/**
 * Extracts the transmission type from a list of options.
 * Finds the first option whose name (lowercased) contains 'transmission',
 * then searches its nested options array for the first non-empty transmissionType.
 * @param {Array} options
 * @returns {string} Uppercased transmissionType, or empty string if not found
 */
export function getTransmissionTypeFromOptions(options = []) {
  const transmissionOption = options.find(({ name }) => name?.toLowerCase().includes('transmission'));
  if (!transmissionOption) return '';

  const nestedOption = transmissionOption.options?.find(({ transmission }) => transmission?.transmissionType);
  return nestedOption?.transmission?.transmissionType?.toUpperCase() || '';
}

/**
 * Filters list of options.
 * Returns only options where Price object is not empty and price > 0.
 * @param {Object} options
 * @returns {Object}
 */
export function filterOptionsWithPrice(options = []) {
  return options.filter(isOptionWithPrice);
}

/**
 * Filters list of options.
 * Returns only Engine and Transmission options or where Price object is not empty and price > 0.
 * @param {Object} options
 * @returns {Object}
 */
export function filterEngineAndTransmissionOrWithPriceOptions(options = []) {
  return options.filter(option => isOptionWithPrice(option) || isEngineOrTransmissionOption(option));
}

/**
 * Gets color array length filtered by existing RGB.
 * @param {object[]} colors
 */
export const getColorsWithRgbLength = (colors = [], showColors = true) => {
  return showColors ? colors.filter(({ properties }) => properties && properties.rgb).length : 0;
};

export const getOptionsToDisplay = (options = [], isExtendedReport, showOptions = true) => {
  if (!showOptions) {
    return [];
  }

  if (isExtendedReport) {
    return filterEngineAndTransmissionOrWithPriceOptions(options);
  }

  return filterOptionsWithPrice(options);
};

const buildFeaturesKey = features =>
  features
    ?.map(f => f.description)
    ?.sort()
    ?.join('|') || '';

/**
 * Deduplicates packages by their oemName and genericFeatures (comparing description of each
 * feature, order-insensitively), keeping the first occurrence.
 * @param {Array} packages
 * @returns {Array}
 */
export const deduplicatePackages = (packages = []) => {
  const keys = new Map(packages.map(pkg => [pkg.id, buildFeaturesKey(pkg.genericFeatures)]));
  const seenOemNames = new Set();
  const seenFeatureKeys = new Set();
  return packages.filter(pkg => {
    const key = keys.get(pkg.id);
    if (seenOemNames.has(pkg.oemName) || seenFeatureKeys.has(key)) return false;
    seenOemNames.add(pkg.oemName);
    seenFeatureKeys.add(key);
    return true;
  });
};

/**
 * Returns packages valid for display:
 * - Deduplicates by genericFeatures (id or description of each feature, order-insensitively)
 * - Excludes packages with null or empty genericFeatures
 * - Excludes packages where none of the genericFeature IDs appear in displayedOptions
 * @param {Array} packages
 * @param {Array} displayedOptions - options with price > 0 (from filterOptionsWithPrice)
 * @returns {Array}
 */
export const getPackagesToDisplay = (packages = [], displayedOptions = []) => {
  const displayedOptionIds = new Set(displayedOptions.map(({ id }) => id));
  const filteredPackages = packages.filter(
    ({ genericFeatures }) => genericFeatures?.length > 0 && genericFeatures.some(({ id }) => displayedOptionIds.has(id))
  );
  const sortedPackages = filteredPackages.toSorted((a, b) => (a.oemName < b.oemName ? -1 : 1));
  return deduplicatePackages(sortedPackages);
};

/**
 * Checks whether all of a package's genericFeatures are currently selected.
 * @param {Object} pkg
 * @param {Array} options
 * @returns {boolean}
 */
export const isPackageSelected = (pkg, options) =>
  !!pkg.genericFeatures?.length && pkg.genericFeatures.every(({ id }) => options.find(opt => opt.id === id)?.selected);

/**
 * Builds a Map from feature ID to the Set of package oemNames that contain it.
 * @param {Array} packages
 * @returns {Map<string, Set<string>>}
 */
export const buildFeatureToPackagesMap = (packages = []) => {
  const map = new Map();
  packages.forEach(pkg => {
    pkg.genericFeatures?.forEach(f => {
      if (!map.has(f.id)) map.set(f.id, new Set());
      map.get(f.id).add(pkg.oemName);
    });
  });
  return map;
};

/**
 * Builds a Map from oemName to package object.
 * @param {Array} packages
 * @returns {Map<string, Object>}
 */
export const buildPkgByOemNameMap = (packages = []) => new Map(packages.map(pkg => [pkg.oemName, pkg]));

const getVal = (collection, key) => {
  return collection instanceof Map ? collection.get(key) : collection[key];
};

/**
 * Computes the Set of feature IDs to deselect when a package is unchecked,
 * accounting for cascade-deselection and protection of features shared with
 * still-selected packages.
 * @param {Object} pkg - the package being deselected
 * @param {Array} options - current options state
 * @param {Map<string, Set<string>>|Object<string, Array<string>>} featureToPackages
 * @param {Map<string, Object>|Object} pkgByOemName
 * @param {Array} packagesToDisplay
 * @returns {Set<string>}
 */
export const getPackageDeselectionIds = (pkg, options, featureToPackages, pkgByOemName, packagesToDisplay) => {
  // Pre-compute selection state for every package once
  const isSelectedByOemName = new Map(packagesToDisplay.map(p => [p.oemName, isPackageSelected(p, options)]));

  // Collect the IDs of all features belonging to the package being deselected.
  const featureIds = new Set(pkg.genericFeatures?.map(({ id }) => id) ?? []);

  // Find other packages that share at least one feature with the deselected package.
  // These are cascade candidates: if they are currently fully selected they must also be deselected,
  // because removing a shared feature makes them incomplete.
  const cascadeCandidateNames = new Set();
  featureIds.forEach(fId => {
    getVal(featureToPackages, fId)?.forEach(name => {
      if (name !== pkg.oemName) cascadeCandidateNames.add(name);
    });
  });

  // Among the candidates, keep only those that are actually fully selected right now.
  // Their features are added to the deselection set (cascade effect).
  const selectedCascadePkgNames = new Set();
  const allIdsToUnselect = new Set(featureIds);

  cascadeCandidateNames.forEach(name => {
    if (isSelectedByOemName.get(name)) {
      selectedCascadePkgNames.add(name);
      getVal(pkgByOemName, name)?.genericFeatures?.forEach(({ id }) => allIdsToUnselect.add(id));
    }
  });

  // Identify features that must be protected: they belong to packages that are fully selected
  // but are NOT being cascade-deselected (i.e. they don't share a feature with the deselected package).
  // Removing these features would silently break a package the user still wants checked.
  const protectedIds = new Set();
  packagesToDisplay.forEach(p => {
    if (p.oemName !== pkg.oemName && !selectedCascadePkgNames.has(p.oemName) && isSelectedByOemName.get(p.oemName)) {
      p.genericFeatures?.forEach(({ id }) => protectedIds.add(id));
    }
  });

  // Final deselection set: everything that should be unchecked minus protected features.
  return new Set([...allIdsToUnselect].filter(id => !protectedIds.has(id)));
};
